"use client";

// These files are the single source of truth for BOTH distribution channels
// of the Magnus floating shell: the `@bg-bgi/magnus-ui` npm package
// (packages/sdk/ui inlines them at build time) and the shadcn registry
// (`magnus-floating` block). Edit here; never fork.
//
// This is the publishable host-capability seam. The private `@magnus/react`
// re-exports it and layers its tRPC-typed `data` slot on top — the `TData`
// generic below is that extension point, so the published package never
// references the private `AppRouter` type.

import {
  type AnchorHTMLAttributes,
  type ComponentType,
  createContext,
  createElement,
  type ImgHTMLAttributes,
  type ReactNode,
  use,
  useMemo,
} from "react";

/** Props a Magnus-rendered link receives. Mirrors an anchor plus `href`. */
export type MagnusLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
};

/**
 * Navigation a Magnus surface uses for in-app links (knowledge pages, skills,
 * etc.). The host injects its router (e.g. Next `<Link>` / `useRouter`); the
 * SDK falls back to a plain `<a>` + `location.assign` when unset.
 */
export interface MagnusNavigation {
  Link?: ComponentType<MagnusLinkProps>;
  navigate?: (href: string) => void;
  /**
   * Reactive current pathname hook (e.g. Next's `usePathname`). Must be a
   * stable function reference. Falls back to a non-reactive
   * `window.location.pathname` read when unset.
   */
  usePathname?: () => string;
}

/** Props a Magnus-rendered image receives. Mirrors an `<img>` plus required `src`/`alt`. */
export type MagnusImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  alt: string;
};

/**
 * Image rendering a Magnus surface uses (attachment thumbnails, skill
 * avatars). The host may inject an optimized component (e.g. Next `<Image>`);
 * the SDK falls back to a plain `<img>` when unset.
 */
export interface MagnusMedia {
  Image?: ComponentType<MagnusImageProps>;
}

/**
 * A minimal key/value store for Magnus UI preferences (selected model, voice,
 * etc.). The host may inject its own (e.g. synced storage); the SDK defaults
 * to `localStorage` with cross-tab `storage`-event subscriptions.
 */
export interface MagnusPersistence {
  get(key: string): string | null;
  remove(key: string): void;
  set(key: string, value: string): void;
  /** Subscribe to external changes for `key`. Returns an unsubscribe fn. */
  subscribe(key: string, listener: () => void): () => void;
}

/**
 * The side panel a Magnus tool view opens auxiliary UI into. The SDK ships
 * the tool views and the content they render; the consuming app implements
 * where that content mounts.
 */
export interface MagnusHostSidePanel {
  /** Close the panel if open. */
  close(): void;
  /** Open an interactive panel beside the chat with arbitrary content. */
  open(content: ReactNode, title?: string): void;
  /** Update the title of the currently open panel. */
  setTitle(title: string | null): void;
}

/**
 * Host UI capabilities. `TData` is an opaque extension slot for richer
 * runtimes (the private `@magnus/react` types it to its tRPC client);
 * published consumers can ignore it.
 */
export interface MagnusHostCapabilities<TData = unknown> {
  data?: TData;
  media?: MagnusMedia;
  navigation?: MagnusNavigation;
  persistence?: MagnusPersistence;
  sidePanel: MagnusHostSidePanel;
}

function warnMissing(capability: string): void {
  if (typeof console !== "undefined") {
    console.warn(
      `[Magnus] A tool requested host.${capability} but no MagnusHostProvider supplied it. Mount <MagnusHostProvider> with this capability.`
    );
  }
}

const NOOP_HOST: MagnusHostCapabilities = {
  sidePanel: {
    open: () => warnMissing("sidePanel.open"),
    close: () => warnMissing("sidePanel.close"),
    setTitle: () => warnMissing("sidePanel.setTitle"),
  },
};

const MagnusHostContext = createContext<MagnusHostCapabilities>(NOOP_HOST);

export interface MagnusHostProviderProps<TData = unknown>
  extends MagnusHostCapabilities<TData> {
  children: ReactNode;
}

/**
 * Supplies host UI capabilities to the Magnus surfaces rendered beneath it.
 * Mount this inside whatever app context owns the side panel (so the
 * capabilities can be read from those contexts) and above the chat.
 */
export function MagnusHostProvider<TData = unknown>({
  children,
  sidePanel,
  data,
  media,
  navigation,
  persistence,
}: MagnusHostProviderProps<TData>) {
  // Stable context value — a fresh object here would invalidate every
  // `useMagnusHost()` consumer on each parent render.
  const value = useMemo(
    () => ({ sidePanel, data, media, navigation, persistence }),
    [sidePanel, data, media, navigation, persistence]
  );
  return (
    <MagnusHostContext.Provider value={value}>
      {children}
    </MagnusHostContext.Provider>
  );
}

/** Access host UI capabilities from inside a Magnus surface. */
export function useMagnusHost<
  TData = unknown,
>(): MagnusHostCapabilities<TData> {
  return use(MagnusHostContext) as MagnusHostCapabilities<TData>;
}

function DefaultLink({ href, children, ...rest }: MagnusLinkProps) {
  return (
    <a href={href} {...rest}>
      {children}
    </a>
  );
}

const DEFAULT_NAVIGATION: Required<MagnusNavigation> = {
  Link: DefaultLink,
  navigate: (href) => {
    if (typeof window !== "undefined") {
      window.location.assign(href);
    }
  },
  usePathname: () =>
    typeof window === "undefined" ? "" : window.location.pathname,
};

/**
 * Navigation for in-app links, always defined. Returns the host's router when
 * supplied, otherwise a plain `<a>` + `location.assign` fallback.
 */
export function useMagnusNavigation(): Required<MagnusNavigation> {
  const { navigation } = useMagnusHost();
  const link = navigation?.Link ?? DEFAULT_NAVIGATION.Link;
  const navigate = navigation?.navigate ?? DEFAULT_NAVIGATION.navigate;
  const usePathname = navigation?.usePathname ?? DEFAULT_NAVIGATION.usePathname;
  return useMemo(
    () => ({ Link: link, navigate, usePathname }),
    [link, navigate, usePathname]
  );
}

function DefaultImage({ src, alt, ...rest }: MagnusImageProps) {
  // Portable fallback; uses createElement so the host can inject an optimized
  // Image (e.g. next/image) without the SDK depending on a framework.
  return createElement("img", { src, alt, ...rest });
}

const DEFAULT_MEDIA: Required<MagnusMedia> = {
  Image: DefaultImage,
};

/**
 * Image component for Magnus surfaces, always defined. Returns the host's
 * optimized component when supplied, otherwise a plain `<img>` fallback.
 */
export function useMagnusMedia(): Required<MagnusMedia> {
  const { media } = useMagnusHost();
  const Image = media?.Image ?? DEFAULT_MEDIA.Image;
  return useMemo(() => ({ Image }), [Image]);
}

const DEFAULT_PERSISTENCE: MagnusPersistence = {
  get: (key) => {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // best-effort: storage can throw in privacy modes
    }
  },
  remove: (key) => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.removeItem(key);
    } catch {
      // best-effort
    }
  },
  subscribe: (key, listener) => {
    if (typeof window === "undefined") {
      return () => {
        // no-op on the server
      };
    }
    const handler = (e: StorageEvent) => {
      // `e.key === null` is fired by `localStorage.clear()` — treat it as a
      // change to every key so subscribers re-read after a cross-tab clear.
      if (e.key === key || e.key === null) {
        listener();
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  },
};

/**
 * Key/value store for Magnus UI preferences, always defined. Returns the
 * host's store when supplied, otherwise a `localStorage`-backed default.
 */
export function useMagnusPersistence(): MagnusPersistence {
  const { persistence } = useMagnusHost();
  return persistence ?? DEFAULT_PERSISTENCE;
}
